<i18n src="./TabQuoteDetail.txt" lang="yaml"></i18n>
<script src="./TabQuoteDetail.js"></script>
<style lang="scss" src="./TabQuoteDetail.scss"></style>

<template>
    <div class="myaccount-content">
    <Spinner v-if="isShowSpinner"/>
    <Toast v-if="toastMessage" :message="toastMessage" :type="toastType" />
    <div v-if="quote">
      <div class="col-12 px-0 mx-0 mb-4 pb-2 d-flex justify-content-between align-items-center border-bottom">
        <div class="d-flex align-items-center">
          <a href="javascript:history.go(-1)" class="mr-2 font-weight-bold">&#8592;</a>
          <h3 class="p-0 m-0 border-0">{{ t('title') }}</h3>
        </div>
        <button v-if="!isQuoteOrdered && quote.stagedQuoteState && quote.stagedQuoteState === 'Sent' && !isSellerUser" @click="showModal('acceptAndOrder', {quote: quote})">Accept and Order</button>
        <div class="d-flex col-6 justify-content-around" v-if="quote.quoteRequestState && quote.quoteRequestState === 'Submitted' && isSellerUser">
          <button @click="confirmAcceptQuoteRequest">Accept</button>
          <button @click="confirmRejectQuoteRequest">Reject</button>
        </div>
      </div>
      <div class="row your-quote-details">
        <div class="alert alert-warning col-12" role="alert" v-if="quote.stagedQuoteState && quote.stagedQuoteState === 'InProgress'">
          <p v-if="!isSellerUser">Quote creation by the seller is in progress. Please check after some time!</p>
          <p v-if="isSellerUser" class="d-flex justify-content-between">
            <span class="col-7 px-0">Staged quote is in progress for this request. If completed please send the quote to buyer. </span>
            <button @click="sendFinalQuoteDirect">Send</button>
            <button @click="modifyQuotationCart">Modify</button>
          </p>
        </div>
        <div class="alert alert-success col-12" role="alert" v-if="isQuoteOrdered">
          Quote has been accepted and placed {{ isSellerUser ? 'by the buyer' : ''}}.
        </div>
        <div class="col-12 d-flex px-0" v-if="quote.quoteRequestState">
          <div class="col-4">{{ t('quoteRequestStatus') }}</div>
          <div class="col-7">{{ quote.quoteRequestState }}</div>
        </div>
        <div class="col-12 d-flex px-0">
          <div class="col-4">{{ t('date') }}</div>
          <div class="col-7" v-if="quote.createdAt">
            <BaseDate
              :date="quote.createdAt"
              :format="'short'"
              data-test="details-quote-date"
            />
          </div>
        </div>
        <div class="col-12 d-flex px-0" v-if="quote.comment">
          <div class="col-4">{{t('buyerComments')}}</div>
          <div class="col-7 text-justify"><strong>{{ quote.comment }}</strong></div>
        </div>
        <div class="col-12 d-flex px-0" v-if="quote.buyerComment">
          <div class="col-4">{{t('buyerComments')}}</div>
          <div class="col-7 text-justify"><strong>{{ quote.buyerComment }}</strong></div>
        </div>
        <div class="col-12 d-flex px-0" v-if="quote.sellerComment">
          <div class="col-4">{{t('sellerComments')}}</div>
          <div class="col-7 text-justify"><strong>{{ quote.sellerComment }}</strong></div>
        </div>
      </div>
      <div class="row pt-30">
        <div class="col-md-5 address-detail">
          <b>{{ t('shippingAddress') }}</b>
          <BaseAddress
            class="mt-15"
            :address="quote.shippingAddress"
            data-test="summary-shipping-address"
            v-if="quote.shippingAddress"
          />
        </div>
        <div class="col-md-1"></div>
        <div class="col-md-5 address-detail">
          <b>{{ t('billingAddress') }}</b>
          <BaseAddress
            class="mt-15"
            :address="quote.billingAddress ? quote.billingAddress : quote.shippingAddress"
            data-test="summary-billing-address"
            v-if="(quote.shippingAddress || quote.billingAddress)"
          />
        </div>
      </div>
      <div class="row pt-30">
        <div class="col-md-5 address-detail">
          <b>{{ t('shippingMethod') }}</b>
          <p class="mt-15">
            {{ quote.shippingInfo?.shippingMethod?.name }}
            -
            {{
              quote.shippingInfo?.shippingMethod
                ?.localizedDescription
            }}
          </p>
        </div>
        <div class="col-md-1"></div>
        <div class="col-md-5 address-detail" v-if="paymentInfo">
          <b>{{ t('paymentDetails') }}</b>
          <p class="mt-15">{{ paymentInfo }}</p>
        </div>
      </div>
      <div class="mt-40">
        <b>{{ t('orderItemsTitle') }}</b>
        <CartLikeContentDetail
          :editable="false"
          :cart="quote"
        />
      </div>

      <div class="offset-md-6 text-right subtotal-price">
        <div class="row">
          <span class="col-md-6 title">{{
            t('subtotal')
          }}</span>
          <span class="col-md-6 title">
            <BaseMoney
              :money="subtotal"
              data-test="order-subtotal-price"
            />
          </span>
        </div>
        <div class="row">
          <p class="col-md-6">{{ t('shipping') }}</p>
          <span class="col-md-6">
            <BaseMoney
              :money="quote.shippingInfo?.price"
              data-test="cart-shipping-price"
            />
          </span>
        </div>
        <div
          v-if="quote.discountCodes?.length > 0"
          class="row"
        >
          <p class="col-md-6">
            {{ t('appliedDiscounts') }}
          </p>
          <p
            v-for="discount in quote.discountCodes"
            :key="discount.discountCode.id"
            class="col-md-6"
          >
            {{ discount.discountCode.name }}
          </p>
        </div>
      </div>
      <div class="offset-md-6 text-right total-price">
        <div class="row">
          <span class="col-md-6 title">{{
            t('total')
          }}</span>
          <span class="col-md-6 title">
            <BaseMoney
              :money="quote.totalPrice"
              data-test="cart-total-price"
            />
          </span>
        </div>
      </div>
    </div>
    <div class="pt-50 pb-55" v-if="(!loading && !quote)">
      <h1 class="text-center">{{ t('notFound') }}</h1>
    </div>
    <Modal 
      v-if="isModalVisible" 
      size="sm"
      :secondaryBtn="{text: 'Cancel', callback: hideModal}"
      :primaryBtn="{text: 'Proceed', callback: proceedToOrder}"
      :closeModalCallback="hideModal"
      :message="modalMessage"
      :title="modalTitle"
      />

      <Modal
        v-if="isEditQuotationCart"
        :closeModalCallback="hideModal"
        :type="'modifyQuotationCart'"
        :primaryBtn="{text: 'Send', callback: sendFinalQuote}"
        :secondaryBtn="{text: 'Cancel', callback: hideModal}"
        :title="'Edit Quotation Cart'"
      >
      <QuotationCart :quoteRefetch="quoteRefetch" ref="quotationCartRef"/>
      </Modal>
  </div>
</template>